import { useState, useEffect } from 'react'
import { dealListService } from '../../iso-deal-list/services/dealListService'
import { DealListResponse } from '../../iso-deal-list/types'

export interface DashboardStats {
  totalDeals: number
  totalSubmissions: number
  pendingSubmissions: number
  approvedSubmissions: number
  completedSubmissions: number
}

interface UseDashboardStatsReturn {
  stats: DashboardStats
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export const useDashboardStats = (): UseDashboardStatsReturn => {
  const [stats, setStats] = useState<DashboardStats>({
    totalDeals: 0,
    totalSubmissions: 0,
    pendingSubmissions: 0,
    approvedSubmissions: 0,
    completedSubmissions: 0
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const calculateStats = (data: DealListResponse): DashboardStats => {
    const opportunities = data.opportunities || []
    let totalSubmissions = 0
    let pendingSubmissions = 0
    let approvedSubmissions = 0
    let completedSubmissions = 0

    // Count submissions and their statuses
    opportunities.forEach(opportunity => {
      const submissions = opportunity.submissions || []
      totalSubmissions += submissions.length

      submissions.forEach(submission => {
        const status = submission.status?.toLowerCase()
        
        if (status === 'pending' || status === 'processing') {
          pendingSubmissions++
        } else if (status === 'approved') {
          approvedSubmissions++
        } else if (status === 'completed') {
          completedSubmissions++
        }
      })
    })

    return {
      totalDeals: opportunities.length,
      totalSubmissions,
      pendingSubmissions,
      approvedSubmissions,
      completedSubmissions
    }
  }

  const fetchStats = async () => {
    try {
      setLoading(true)
      setError(null)
      
      console.log('📊 Fetching dashboard stats...')
      const response = await dealListService.getISODeals()
      const calculatedStats = calculateStats(response.data)
      
      console.log('✅ Dashboard stats calculated:', calculatedStats)
      setStats(calculatedStats)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard stats'
      setError(errorMessage)
      console.error('❌ Error fetching dashboard stats:', err)
    } finally {
      setLoading(false)
    }
  }

  const refresh = async () => {
    await fetchStats()
  }

  useEffect(() => {
    fetchStats()
  }, [])

  return {
    stats,
    loading,
    error,
    refresh
  }
}
